import apiClient from './apiClient';

// ── Raw backend shapes (unitPriceUsd arrives as string from pg decimal) ──
interface RawLatestPrice {
  game: string;
  league: string;
  itemName: string;
  unitPriceUsd: string;
  sellerName: string;
  recordedAt: string;
}

interface RawHistoryPoint {
  recordedAt: string;
  unitPriceUsd: string;
}

// ── Normalized (consumable) shapes ──
export interface G2gLatestPrice {
  game: string;
  league: string;
  itemName: string;
  unitPriceUsd: number | null;
  sellerName: string;
  recordedAt: string;
}

export interface G2gHistoryPoint {
  recordedAt: string;
  unitPriceUsd: number | null;
}

// ── Normalization helpers ──
function parseUsd(raw: string): number | null {
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : null;
}

function normalizeLatestPrice(raw: RawLatestPrice): G2gLatestPrice {
  return { ...raw, unitPriceUsd: parseUsd(raw.unitPriceUsd) };
}

function normalizeHistoryPoint(raw: RawHistoryPoint): G2gHistoryPoint {
  return { ...raw, unitPriceUsd: parseUsd(raw.unitPriceUsd) };
}

// ── Public API ──
export async function fetchG2gLatestPrices(
  game: 'PoE1' | 'PoE2',
  signal?: AbortSignal,
): Promise<G2gLatestPrice[]> {
  const response = await apiClient.get<RawLatestPrice[]>('/g2g-prices/latest', {
    params: { game },
    signal,
  });
  return response.data.map(normalizeLatestPrice);
}

export async function fetchG2gPriceHistory(
  game: 'PoE1' | 'PoE2',
  league: string,
  hours = 24,
  signal?: AbortSignal,
): Promise<G2gHistoryPoint[]> {
  const response = await apiClient.get<RawHistoryPoint[]>('/g2g-prices/history', {
    params: { game, league, hours },
    signal,
  });
  return response.data.map(normalizeHistoryPoint);
}

/**
 * Convenience: fetch latest + per-league history in one call.
 * Returns latest prices (one per league) and a map of league → history points.
 *
 * Per-league history requests use Promise.allSettled so that a single league's
 * history failure does not discard latest prices or other leagues' histories.
 * Leagues whose history failed quietly yield an empty array. The page component
 * renders whatever data is available — a league with missing history still
 * appears in the stat cards but is omitted from the chart series.
 */
export async function fetchG2gPageData(
  game: 'PoE1' | 'PoE2',
  signal?: AbortSignal,
): Promise<{ latest: G2gLatestPrice[]; history: Record<string, G2gHistoryPoint[]> }> {
  const latest = await fetchG2gLatestPrices(game, signal);

  // Fetch 24h history for every league that appeared in the latest response.
  const leagues = [...new Set(latest.map(p => p.league))];
  const historyResults = await Promise.allSettled(
    leagues.map(async (league) => {
      const points = await fetchG2gPriceHistory(game, league, 24, signal);
      return [league, points] as const;
    }),
  );

  const history: Record<string, G2gHistoryPoint[]> = {};
  for (const result of historyResults) {
    if (result.status === 'fulfilled') {
      const [league, points] = result.value;
      history[league] = points;
    }
    // If a league's history fails, it simply won't appear in the chart,
    // but the stat card (from /latest) still shows.
  }

  return { latest, history };
}

// ── Price statistics helpers ──

export interface PriceStats {
  currentPrice: number | null;
  change24h: number | null;
  change3d: number | null;
  change7d: number | null;
  min24h: number | null;
  max24h: number | null;
  variance24h: number | null;
  avg1h: number | null;
  dataPoints24h: number;
  dataPoints3d: number;
  dataPoints7d: number;
  dataPoints1h: number;
}

/**
 * Compute price statistics from a history data array.
 * `data` is sorted by recordedAt ascending (as returned by the API).
 * `recordedAt` is an ISO string; `unitPriceUsd` is already a parsed number.
 */
export function computePriceStats(data: Array<{ recordedAt: string; unitPriceUsd: number }>): PriceStats {
  if (!data || data.length === 0) {
    return { currentPrice: null, change24h: null, change3d: null, change7d: null, min24h: null, max24h: null, variance24h: null, avg1h: null, dataPoints24h: 0, dataPoints3d: 0, dataPoints7d: 0, dataPoints1h: 0 };
  }

  const now = new Date(data[data.length - 1].recordedAt).getTime();
  const currentPrice = data[data.length - 1].unitPriceUsd;

  // Helper: find oldest price within time window (hours ago from latest data point)
  const getPriceHoursAgo = (hours: number): number | null => {
    const cutoff = now - hours * 3600_000;
    const point = data.find(d => new Date(d.recordedAt).getTime() >= cutoff);
    return point ? point.unitPriceUsd : null;
  };

  // Helper: get data points within time window
  const getWindow = (hours: number) => {
    const cutoff = now - hours * 3600_000;
    return data.filter(d => new Date(d.recordedAt).getTime() >= cutoff);
  };

  const price24hAgo = getPriceHoursAgo(24);
  const price3dAgo = getPriceHoursAgo(72);
  const price7dAgo = getPriceHoursAgo(168);

  const window24h = getWindow(24);
  const window1h = getWindow(1);

  const prices24h = window24h.map(d => d.unitPriceUsd);
  const min24h = prices24h.length > 0 ? Math.min(...prices24h) : null;
  const max24h = prices24h.length > 0 ? Math.max(...prices24h) : null;

  const avg1h = window1h.length > 0
    ? window1h.reduce((sum, d) => sum + d.unitPriceUsd, 0) / window1h.length
    : null;

  return {
    currentPrice,
    change24h: price24hAgo ? ((currentPrice - price24hAgo) / price24hAgo) * 100 : null,
    change3d: price3dAgo ? ((currentPrice - price3dAgo) / price3dAgo) * 100 : null,
    change7d: price7dAgo ? ((currentPrice - price7dAgo) / price7dAgo) * 100 : null,
    min24h,
    max24h,
    variance24h: min24h ? ((max24h! - min24h) / min24h) * 100 : null,
    avg1h,
    dataPoints24h: prices24h.length,
    dataPoints3d: getPriceHoursAgo(72) !== null ? getWindow(72).length : 0,
    dataPoints7d: getPriceHoursAgo(168) !== null ? getWindow(168).length : 0,
    dataPoints1h: window1h.length,
  };
}

// ── Admin delete helpers ──

export interface G2GDeletePreview {
  count: number;
}

export async function fetchG2GPriceCount(
  params: { start: string; end: string; game?: string; leagues?: string[] },
  signal?: AbortSignal,
): Promise<G2GDeletePreview> {
  const q = new URLSearchParams({ start: params.start, end: params.end });
  if (params.game) q.set('game', params.game);
  if (params.leagues?.length) q.set('leagues', params.leagues.join(','));
  const response = await apiClient.get<G2GDeletePreview>(`/g2g-prices/count?${q}`, { signal });
  return response.data;
}

export async function deleteG2GPrices(
  params: { start: string; end: string; game?: string; leagues?: string[] },
  signal?: AbortSignal,
): Promise<{ deleted: number }> {
  return apiClient.delete('/g2g-prices', {
    data: params,
    signal,
  });
}